/**
 * =============================================================================
 * URL STATE HOOK - URL-Driven State Architecture for Workspaces
 * =============================================================================
 * 
 * PHASE 2A: Platform Interaction Foundation
 * 
 * All workspace state must be URL-driven for:
 * - Refresh-safe state persistence
 * - Deep linking / shareable URLs
 * - Browser back/forward compatibility
 * - Operational continuity
 * 
 * Examples:
 * /phs/helpdesk?status=awaiting-quote&search=plumbing
 * /phs/finance?view=invoice-review&status=pending
 * /phs/assets?tab=compliance&status=expired
 * /phs/contractors?tab=performance&score=poor
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Custom hook for URL-driven state management
 * 
 * @param {Object} config - Configuration for URL params
 * @param {Object} config.defaultParams - Default parameter values
 * @param {string[]} config.persistKeys - Keys that should be persisted to URL
 * 
 * @returns {Object} - { params, setParams, updateParams, clearParams, getParam }
 */
export function useUrlState(config = {}) {
  const { defaultParams = {}, persistKeys = [] } = config;
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current params with defaults
  const params = useMemo(() => {
    const current = {};
    
    // First apply defaults
    Object.keys(defaultParams).forEach(key => {
      current[key] = defaultParams[key];
    });
    
    // Then override with URL params
    for (const [key, value] of searchParams.entries()) {
      // Decode URI-encoded values
      current[key] = decodeURIComponent(value);
    }
    
    return current;
  }, [searchParams, defaultParams]);

  // Get a single param value
  const getParam = useCallback((key, fallback = null) => {
    return params[key] ?? defaultParams[key] ?? fallback;
  }, [params, defaultParams]);

  // Set all params (replaces existing)
  const setParams = useCallback((newParams) => {
    const filtered = {};
    Object.entries(newParams).forEach(([key, value]) => {
      // Only include non-default, non-empty values
      if (value !== null && value !== undefined && value !== '' && value !== defaultParams[key]) {
        filtered[key] = value;
      }
    });
    setSearchParams(filtered);
  }, [setSearchParams, defaultParams]);

  // Update specific params (merges with existing)
  const updateParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '' || value === defaultParams[key]) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    
    setSearchParams(newParams);
  }, [searchParams, setSearchParams, defaultParams]);

  // Clear specific params or all params
  const clearParams = useCallback((keys = null) => {
    if (keys === null) {
      setSearchParams({});
    } else {
      const newParams = new URLSearchParams(searchParams);
      keys.forEach(key => newParams.delete(key));
      setSearchParams(newParams);
    }
  }, [searchParams, setSearchParams]);

  // Check if a param has a specific value
  const hasParam = useCallback((key, value = null) => {
    const paramValue = searchParams.get(key);
    if (value === null) {
      return paramValue !== null;
    }
    return paramValue === value;
  }, [searchParams]);

  return {
    params,
    setParams,
    updateParams,
    clearParams,
    getParam,
    hasParam,
    searchParams, // Raw URLSearchParams for advanced use
  };
}

/**
 * Workspace-specific hooks with sensible defaults
 */

// Helpdesk Dashboard URL State
export function useHelpdeskUrlState() {
  return useUrlState({
    defaultParams: {
      status: '',        // e.g., 'awaiting-quote', 'overdue', 'Job Created'
      search: '',        // Search term
      view: 'table',     // 'table' | 'board' | 'card'
      priority: '',      // 'emergency' | 'high' | 'medium' | 'low'
      contractor: '',    // Contractor ID filter
      park: '',          // Park ID filter
      sort: 'created',   // Sort field
      order: 'desc',     // Sort order
    },
  });
}

// Asset Workspace URL State
export function useAssetUrlState() {
  return useUrlState({
    defaultParams: {
      tab: 'parks',      // 'parks' | 'plots' | 'compliance' | 'ppm' | 'register'
      search: '',        // Search term
      status: '',        // e.g., 'expired', 'expiring', 'compliant'
      park: '',          // Filter by park ID
      type: '',          // Asset type filter
      selected: '',      // Selected item ID for detail view
    },
  });
}

// Contractor Workspace URL State
export function useContractorUrlState() {
  return useUrlState({
    defaultParams: {
      tab: 'directory',  // 'directory' | 'performance' | 'compliance'
      search: '',        // Search term
      trade: '',         // Trade filter
      status: '',        // 'active' | 'inactive' | 'suspended'
      score: '',         // 'excellent' | 'good' | 'poor'
      selected: '',      // Selected contractor ID
    },
  });
}

// Finance Workspace URL State
export function useFinanceUrlState() {
  return useUrlState({
    defaultParams: {
      view: 'overview',  // 'overview' | 'awaiting-invoice' | 'invoice-review' | 'payments'
      search: '',        // Search term
      status: '',        // 'pending' | 'approved' | 'paid' | 'overdue'
      contractor: '',    // Contractor ID filter
      dateFrom: '',      // Date range start
      dateTo: '',        // Date range end
      selected: '',      // Selected invoice ID
    },
  });
}

export default useUrlState;
